import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Search, 
  Download, 
  RefreshCw, 
  Filter,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  FileText,
  Clock,
  CheckCircle,
  Star,
  Calendar,
  User,
  TrendingUp,
  X,
  ArrowRight,
  Circle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import * as XLSX from 'xlsx';
import './SeguimientoCasosInstitucion.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SeguimientoCasosInstitucion = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gestiones, setGestiones] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [expandedInstituciones, setExpandedInstituciones] = useState({});
  const [expandedDependencias, setExpandedDependencias] = useState({});
  const [selectedCaso, setSelectedCaso] = useState(null);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    idCaso: '',
    institucion: '',
    dependencia: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [gestionesRes, institucionesRes] = await Promise.all([
        axios.get(`${API}/gestiones?limit=5000`, { headers }),
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] }))
      ]);

      setGestiones(gestionesRes.data.gestiones || gestionesRes.data || []);
      setInstituciones(institucionesRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Procesar datos agrupados por institución y dependencia
  const datosAgrupados = useMemo(() => {
    const mapa = {};
    
    gestiones.forEach(g => {
      const inst = g.institucion || 'Sin institución';
      const dep = g.dependencia || 'Sin dependencia';
      
      if (!mapa[inst]) {
        mapa[inst] = {
          nombre: inst,
          dependencias: {},
          totalActivos: 0,
          totalCompletados: 0,
          total: 0
        };
      }
      
      if (!mapa[inst].dependencias[dep]) {
        mapa[inst].dependencias[dep] = {
          nombre: dep,
          casos: [],
          activos: 0,
          completados: 0
        };
      }
      
      mapa[inst].dependencias[dep].casos.push(g);
      mapa[inst].total++;
      
      if (g.estado === 'Completada / Archivada') {
        mapa[inst].dependencias[dep].completados++;
        mapa[inst].totalCompletados++;
      } else {
        mapa[inst].dependencias[dep].activos++;
        mapa[inst].totalActivos++;
      }
    });
    
    // Convertir a array y ordenar por total de casos (mayor a menor)
    return Object.values(mapa)
      .map(inst => ({
        ...inst,
        dependencias: Object.values(inst.dependencias).sort((a, b) => b.casos.length - a.casos.length)
      }))
      .sort((a, b) => b.total - a.total);
  }, [gestiones]);

  // Aplicar filtros
  const datosFiltrados = useMemo(() => {
    let resultado = [...datosAgrupados];
    
    if (filtros.institucion) {
      resultado = resultado.filter(inst => 
        inst.nombre.toLowerCase().includes(filtros.institucion.toLowerCase())
      );
    }
    
    if (filtros.dependencia) {
      resultado = resultado.map(inst => ({
        ...inst,
        dependencias: inst.dependencias.filter(dep =>
          dep.nombre.toLowerCase().includes(filtros.dependencia.toLowerCase())
        )
      })).filter(inst => inst.dependencias.length > 0);
    }
    
    if (filtros.idCaso) {
      resultado = resultado.map(inst => ({
        ...inst,
        dependencias: inst.dependencias.map(dep => ({
          ...dep,
          casos: dep.casos.filter(c =>
            c.nomenclatura?.toLowerCase().includes(filtros.idCaso.toLowerCase()) ||
            c.id?.toLowerCase().includes(filtros.idCaso.toLowerCase())
          )
        })).filter(dep => dep.casos.length > 0)
      })).filter(inst => inst.dependencias.length > 0);
    }
    
    return resultado;
  }, [datosAgrupados, filtros]);

  // Obtener valores únicos para filtros
  const valoresUnicos = useMemo(() => {
    const deps = new Set();
    datosAgrupados.forEach(inst => {
      inst.dependencias.forEach(dep => deps.add(dep.nombre));
    });
    return {
      instituciones: datosAgrupados.map(i => i.nombre),
      dependencias: [...deps].sort()
    };
  }, [datosAgrupados]);

  // Toggle expandir institución
  const toggleInstitucion = (nombre) => {
    setExpandedInstituciones(prev => ({
      ...prev,
      [nombre]: !prev[nombre]
    }));
  };

  // Toggle expandir dependencia
  const toggleDependencia = (instNombre, depNombre) => {
    const key = `${instNombre}-${depNombre}`;
    setExpandedDependencias(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Expandir todas las instituciones
  const expandirTodo = () => {
    const expanded = {};
    const expandedDeps = {};
    datosFiltrados.forEach(inst => {
      expanded[inst.nombre] = true;
      inst.dependencias.forEach(dep => {
        expandedDeps[`${inst.nombre}-${dep.nombre}`] = true;
      });
    });
    setExpandedInstituciones(expanded);
    setExpandedDependencias(expandedDeps);
  };

  // Colapsar todo
  const colapsarTodo = () => {
    setExpandedInstituciones({});
    setExpandedDependencias({});
    setSelectedCaso(null);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({ idCaso: '', institucion: '', dependencia: '' });
  };

  // Obtener línea de tiempo del caso
  const getTimelineCaso = (caso) => {
    const eventos = [];
    
    // Creación
    if (caso.createdAt) {
      eventos.push({
        fecha: caso.createdAt,
        tipo: 'creacion',
        titulo: 'Caso Creado',
        descripcion: `Registrado por ${caso.createdBy || 'Sistema'}`,
        icono: <Circle size={12} />
      });
    }
    
    // Asignación CNC
    if (caso.fecha_asignacion) {
      eventos.push({
        fecha: caso.fecha_asignacion,
        tipo: 'asignacion',
        titulo: 'Asignado a CNC',
        descripcion: `Asesor: ${caso.nombre_asesor_cnc || 'N/A'}`,
        icono: <User size={12} />
      });
    }
    
    // Derivación a Probidad
    if (caso.fecha_asignacion_probidad) {
      eventos.push({
        fecha: caso.fecha_asignacion_probidad,
        tipo: 'derivacion',
        titulo: 'Derivado a Probidad',
        descripcion: `Asesor: ${caso.asesor_probidad_nombre || 'N/A'}`,
        icono: <ArrowRight size={12} />
      });
    }
    
    // Avances/Bitácora
    if (caso.bitacora?.length > 0) {
      caso.bitacora.slice(-3).forEach(entry => {
        eventos.push({
          fecha: entry.fecha,
          tipo: 'avance',
          titulo: entry.accion || 'Actualización',
          descripcion: entry.detalles || entry.texto || '',
          icono: <FileText size={12} />
        });
      });
    }
    
    // Completado
    if (caso.estado === 'Completada / Archivada' && caso.updatedAt) {
      eventos.push({
        fecha: caso.updatedAt,
        tipo: 'completado',
        titulo: 'Caso Completado',
        descripcion: caso.calificacion_instancia ? `Calificación: ${caso.calificacion_instancia.puntuacion}/5` : 'Archivado',
        icono: <CheckCircle size={12} />
      });
    }
    
    // Ordenar por fecha
    return eventos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  };

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datos = [];
    
    datosFiltrados.forEach(inst => {
      inst.dependencias.forEach(dep => {
        dep.casos.forEach(caso => {
          datos.push({
            'Institución': inst.nombre,
            'Dependencia': dep.nombre,
            'ID Caso': caso.nomenclatura,
            'Tipo Gestión': caso.tipo_gestion,
            'Estado': caso.estado,
            'Asesor CNC': caso.nombre_asesor_cnc || 'N/A',
            'Asesor Probidad': caso.asesor_probidad_nombre || 'N/A',
            'Fecha Creación': formatLocalDate(caso.createdAt),
            'Fecha Asignación': formatLocalDate(caso.fecha_asignacion),
            'Calificación': caso.calificacion_instancia ? `${caso.calificacion_instancia.puntuacion}/5` : 'N/A'
          });
        });
      });
    });
    
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Seguimiento');
    XLSX.writeFile(wb, `Seguimiento_Instituciones_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  // Calcular totales
  const totales = useMemo(() => {
    return {
      instituciones: datosFiltrados.length,
      casos: datosFiltrados.reduce((acc, i) => acc + i.total, 0),
      activos: datosFiltrados.reduce((acc, i) => acc + i.totalActivos, 0),
      completados: datosFiltrados.reduce((acc, i) => acc + i.totalCompletados, 0)
    };
  }, [datosFiltrados]);

  // Renderizar estrellas
  const renderEstrellas = (puntuacion) => {
    if (!puntuacion) return null;
    return (
      <span className="estrellas">
        {'★'.repeat(puntuacion)}{'☆'.repeat(5 - puntuacion)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="consulta-loading">
        <div className="loader"></div>
        <p>Cargando seguimiento por institución...</p>
      </div>
    );
  }

  return (
    <div className="seguimiento-container" data-testid="seguimiento-casos-institucion">
      <div className="seguimiento-header">
        <div>
          <h1>
            <Building2 size={28} />
            Seguimiento de Casos por Institución
          </h1>
          <p>Vista consolidada con detalle por dependencia y línea de tiempo</p>
        </div>
        <div className="seguimiento-actions">
          <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Actualizar
          </button>
          <button className="btn btn-primary" onClick={handleExportarExcel}>
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Tarjetas de Resumen */}
      <div className="resumen-cards">
        <div className="resumen-card instituciones">
          <Building2 size={24} />
          <div>
            <span className="numero">{totales.instituciones}</span>
            <span className="label">Instituciones</span>
          </div>
        </div>
        <div className="resumen-card total">
          <FileText size={24} />
          <div>
            <span className="numero">{totales.casos}</span>
            <span className="label">Total Casos</span>
          </div>
        </div>
        <div className="resumen-card activos">
          <Clock size={24} />
          <div>
            <span className="numero">{totales.activos}</span>
            <span className="label">Activos</span>
          </div>
        </div>
        <div className="resumen-card completados">
          <CheckCircle size={24} />
          <div>
            <span className="numero">{totales.completados}</span>
            <span className="label">Completados</span>
          </div>
        </div>
      </div>

      {/* Panel de Filtros */}
      <div className="filtros-panel">
        <div className="filtros-content">
          <div className="filtros-grid">
            <div className="filtro-item">
              <label><Search size={14} /> ID de Caso</label>
              <input
                type="text"
                placeholder="Buscar por código..."
                value={filtros.idCaso}
                onChange={(e) => setFiltros({...filtros, idCaso: e.target.value})}
              />
            </div>
            <div className="filtro-item">
              <label><Building2 size={14} /> Institución</label>
              <select
                value={filtros.institucion}
                onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
              >
                <option value="">Todas las instituciones</option>
                {valoresUnicos.instituciones.map(inst => (
                  <option key={inst} value={inst}>{inst}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filtros-actions">
            <button className="btn btn-sm" onClick={expandirTodo}>
              <ChevronDown size={14} /> Expandir Todo
            </button>
            <button className="btn btn-sm" onClick={colapsarTodo}>
              <ChevronUp size={14} /> Colapsar Todo
            </button>
            <button className="btn btn-sm btn-secondary" onClick={limpiarFiltros}>
              <X size={14} /> Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Vista Principal: Instituciones y Dependencias */}
      <div className="instituciones-grid">
        {/* Panel izquierdo: Lista jerárquica */}
        <div className="panel-jerarquico">
          <h3>
            <TrendingUp size={18} />
            Ranking por Volumen de Casos
          </h3>
          
          {/* Leyenda de indicadores */}
          <div className="indicadores-leyenda">
            <span className="leyenda-item activo" title="Casos activos o pendientes de completar">
              <Clock size={12} /> Activos
            </span>
            <span className="leyenda-item completado" title="Casos completados/archivados">
              <CheckCircle size={12} /> Completados
            </span>
            <span className="leyenda-item total" title="Total de casos (activos + completados)">
              <FileText size={12} /> Total
            </span>
          </div>
          
          {datosFiltrados.length === 0 ? (
            <div className="empty-state">
              <Building2 size={48} />
              <p>No se encontraron instituciones con los filtros aplicados</p>
            </div>
          ) : (
            <div className="instituciones-lista">
              {datosFiltrados.map((inst, instIndex) => (
                <div key={inst.nombre} className="institucion-item">
                  <div 
                    className={`institucion-header ${expandedInstituciones[inst.nombre] ? 'expanded' : ''}`}
                    onClick={() => toggleInstitucion(inst.nombre)}
                  >
                    <div className="institucion-info">
                      <span className="ranking-badge">#{instIndex + 1}</span>
                      <div className="institucion-nombre">
                        <strong>{inst.nombre}</strong>
                        <span className="dependencias-count">
                          {inst.dependencias.length} dependencia{inst.dependencias.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="institucion-stats">
                      <span className="stat activo" title="Casos Activos/Pendientes">{inst.totalActivos}</span>
                      <span className="stat completado" title="Casos Completados">{inst.totalCompletados}</span>
                      <span className="stat total" title="Total de Casos">{inst.total}</span>
                      <ChevronRight 
                        size={18} 
                        className={`chevron ${expandedInstituciones[inst.nombre] ? 'rotated' : ''}`}
                      />
                    </div>
                  </div>
                  
                  {expandedInstituciones[inst.nombre] && (
                    <div className="dependencias-lista">
                      {inst.dependencias.map(dep => {
                        const depKey = `${inst.nombre}-${dep.nombre}`;
                        return (
                          <div key={dep.nombre} className="dependencia-item">
                            <div 
                              className={`dependencia-header ${expandedDependencias[depKey] ? 'expanded' : ''}`}
                              onClick={() => toggleDependencia(inst.nombre, dep.nombre)}
                            >
                              <span className="dep-nombre">{dep.nombre}</span>
                              <div className="dep-stats">
                                <span className="stat activo" title="Activos">{dep.activos}</span>
                                <span className="stat completado" title="Completados">{dep.completados}</span>
                                <ChevronRight 
                                  size={14} 
                                  className={`chevron ${expandedDependencias[depKey] ? 'rotated' : ''}`}
                                />
                              </div>
                            </div>
                            
                            {expandedDependencias[depKey] && (
                              <div className="casos-lista">
                                {dep.casos.map(caso => (
                                  <div 
                                    key={caso.id}
                                    className={`caso-item ${selectedCaso?.id === caso.id ? 'selected' : ''} ${caso.estado === 'Completada / Archivada' ? 'completado' : ''}`}
                                    onClick={() => setSelectedCaso(caso)}
                                  >
                                    <div className="caso-info">
                                      <span className="caso-id">{caso.nomenclatura}</span>
                                      <span className="caso-tipo">{caso.tipo_gestion}</span>
                                    </div>
                                    <div className="caso-indicadores">
                                      {caso.estado === 'Completada / Archivada' && (
                                        <>
                                          {caso.calificacion_instancia && renderEstrellas(caso.calificacion_instancia.puntuacion)}
                                          <CheckCircle size={14} className="icono-completado" />
                                        </>
                                      )}
                                      {caso.estado !== 'Completada / Archivada' && (
                                        <Clock size={14} className="icono-activo" />
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel derecho: Detalle y Timeline */}
        <div className="panel-detalle">
          {selectedCaso ? (
            <>
              <div className="detalle-header">
                <h3>
                  <FileText size={18} />
                  {selectedCaso.nomenclatura}
                  {selectedCaso.estado === 'Completada / Archivada' && (
                    <span className="badge-completado">
                      <CheckCircle size={14} /> Completado
                    </span>
                  )}
                </h3>
                <button className="btn-cerrar" onClick={() => setSelectedCaso(null)}>
                  <X size={18} />
                </button>
              </div>
              
              <div className="detalle-info">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Institución</span>
                    <span className="valor">{selectedCaso.institucion}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Dependencia</span>
                    <span className="valor">{selectedCaso.dependencia || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Tipo Gestión</span>
                    <span className="valor">{selectedCaso.tipo_gestion}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Estado</span>
                    <span className={`valor estado ${selectedCaso.estado === 'Completada / Archivada' ? 'completado' : 'activo'}`}>
                      {selectedCaso.estado}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Asesor CNC</span>
                    <span className="valor">{selectedCaso.nombre_asesor_cnc || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Asesor Probidad</span>
                    <span className="valor">{selectedCaso.asesor_probidad_nombre || 'N/A'}</span>
                  </div>
                  {selectedCaso.calificacion_instancia && (
                    <div className="info-item calificacion">
                      <span className="label">Calificación</span>
                      <span className="valor">
                        {renderEstrellas(selectedCaso.calificacion_instancia.puntuacion)}
                        <span className="puntuacion">({selectedCaso.calificacion_instancia.puntuacion}/5)</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="timeline-container">
                <h4>
                  <Calendar size={16} />
                  Línea de Tiempo
                </h4>
                <div className="timeline">
                  {getTimelineCaso(selectedCaso).map((evento, index) => (
                    <div key={index} className={`timeline-item ${evento.tipo}`}>
                      <div className="timeline-dot">
                        {evento.icono}
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-fecha">
                          {formatLocalDate(evento.fecha)}
                        </div>
                        <div className="timeline-titulo">{evento.titulo}</div>
                        {evento.descripcion && (
                          <div className="timeline-descripcion">{evento.descripcion}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-detalle">
              <FileText size={48} />
              <p>Seleccione un caso para ver su detalle y línea de tiempo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeguimientoCasosInstitucion;
